import React, { useMemo, useContext, useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { debounce } from "lodash";
import { isMobile } from "react-device-detect";
import { Alert, FieldGroup, ProductSearch, Separator } from "../../components";
import { UserContext } from "../../contexts";
import { Visible } from "../../core/components";
import { ProductBasicFields, ProductDetailedFields } from "./index.jsx";

const DEBOUNCE_DELAY = 300;

const ProductChoice = ({
  nameObject,
  readOnly = false,
  showHeaders = true,
  fromSearch = false,
  onCategoryChanged,
  onBrandChanged,
  onModelChanged,
  onProductSelected,
  onSearchQueryChange,
}) => {
  if (onProductSelected && onSearchQueryChange) {
    throw new Error(
      '[ProductChoice] Both "onProductSelected" and "onSearchQueryChange" are defined, but only one is required.'
    );
  }

  const { values, setFieldValue } = useFormikContext();
  const [expertiseError, setExpertiseError] = useState(false);
  const { user } = useContext(UserContext);
  const debouncedQueryHandler = useMemo(
    () =>
      typeof onSearchQueryChange === "function"
        ? debounce(onSearchQueryChange, DEBOUNCE_DELAY)
        : null,
    [onSearchQueryChange]
  );

  // todo uncomment whenever there is more than one category
  const {
    // category_id: categoryId,
    brand_id: brandId,
    model_id: modelId,
  } = nameObject ? values[nameObject] : values;

  // Only notify parent when brand/model actually changes, not on every render.
  // The missing-deps lint warning for onBrandChanged/onModelChanged is intentional:
  // those callbacks are arrow functions recreated each render; including them would
  // re-introduce the every-render firing that caused the infinite loop.
  useEffect(() => {
    // onCategoryChanged?.(categoryId);
    onBrandChanged?.(brandId);
    onModelChanged?.(modelId);
  }, [brandId, modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryId = 9;

  // Track whether this is the initial mount so we skip the cascade of
  // setFieldValue calls that would unnecessarily clear model/EAV fields
  // when the form is pre-filled with an existing product's data.
  // On initial mount brand_id is already set from initialValues — there is
  // nothing to reset. The effect should only run when the USER changes brand.
  const isBrandInitialMount = useRef(true);
  useEffect(() => {
    if (isBrandInitialMount.current) {
      isBrandInitialMount.current = false;
      return;
    }
    // todo remove category_id whenever there is more than one category
    setFieldValue("category_id", 9);
    setFieldValue("model_id", null);
    // On the search form the reference is a free-text filter the rep may have
    // typed BEFORE picking a brand (ProductBasicFields surfaces it next to
    // Select Brand when no brand is chosen). Wiping it here would throw that
    // input away the moment they narrow by brand. On the product create/edit
    // form the reference belongs to the brand, so it still resets.
    if (!fromSearch) {
      setFieldValue("reference_number", "");
    }
    setFieldValue("values[case_material]", null);
    setFieldValue("values[case_diameter]", null);
    setFieldValue("values[dial_color]", null);
  }, [values.brand_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onProductSelect = (product) => {
    const userIsExpertOnProductCategory = user.expert_categories.some(
      (c) => c.id === product.category_id
    );

    if (!userIsExpertOnProductCategory && !fromSearch) {
      return setExpertiseError(true);
    }

    setExpertiseError(false);
    onProductSelected(product);
  };

  useEffect(() => {
    if (typeof debouncedQueryHandler === "function") {
      debouncedQueryHandler(values);
    }
  }, [values.query]);

  return (
    <div className="row listing-fields form-section">
      <Visible when={showHeaders}>
        <div className="col-md-3 d-none d-lg-table-row">
          <h3>Product</h3>
        </div>
      </Visible>

      <div className={showHeaders ? "col-md-9" : ""}>
        <Visible when={expertiseError}>
          <Alert message="Your selected work expertise doesn't allow you to upload this category of product." />
        </Visible>

        <div className="row">
          <ProductBasicFields
            {...{ categoryId, brandId, modelId, nameObject, readOnly,fromSearch }}
          />
        </div>

        <Visible when={brandId}>
          <ProductDetailedFields
            {...{ categoryId, nameObject, readOnly, fromSearch }}
          />
        </Visible>

        <Visible when={brandId}>
          <div className="d-flex justify-content-center">
            <Visible when={onSearchQueryChange}>
              <div className={`${isMobile ? "col" : "products"} quick-search`}>
                <FieldGroup
                  name="query"
                  type="text"
                  autoComplete="off"
                  placeholder="Refine"
                />
              </div>
            </Visible>

            <Visible when={onProductSelected}>
              <ProductSearch onSelect={onProductSelect} />
            </Visible>
          </div>
        </Visible>
      </div>
    </div>
  );
};

export default ProductChoice;
